import type { DateAvailabilityOut } from '../../../../api';
import { PaymentModel } from './components/paymentModel';

export enum WizardStep {
  WhatWhen = 1,
  Customer = 2,
  Extras = 3,
}

export enum StepOneError {
  MISSING_DATE = 'MISSING_DATE',
  MISSING_TIME = 'MISSING_TIME',
  END_BEFORE_START = 'END_BEFORE_START',
}

export enum StepThreeError {
  LINK_AMOUNT_REQUIRED = 'LINK_AMOUNT_REQUIRED',
  LINK_AMOUNT_ABOVE_PRICE = 'LINK_AMOUNT_ABOVE_PRICE',
}

const CUSTOMER_FIELDS = [
  'firstname',
  'lastname',
  'alternative_full_name',
  'email',
  'phone',
  'custom_personal_details',
  'voucher_greeting_message',
  'should_send_to_recipient',
  'recipient_email',
  'delivery_date',
  'delivery_time',
  'should_send_delayed',
];

export const stepOfField = (field: string): WizardStep =>
  CUSTOMER_FIELDS.includes(field) ? WizardStep.Customer : WizardStep.WhatWhen;

type StepOneValues = {
  date: string;
  timeSeconds: number;
  privateOrderEndTimeSeconds: number;
};

type StepOneContext = {
  isEventOrCourse: boolean;
  availableDatesForEvents: DateAvailabilityOut[];
};

export const validateStepOne = (
  values: StepOneValues,
  { isEventOrCourse, availableDatesForEvents }: StepOneContext,
): StepOneError | undefined => {
  if (!values.date) {
    return StepOneError.MISSING_DATE;
  }
  if (isEventOrCourse) {
    const selectedDate = availableDatesForEvents.find((x) => x.date === values.date);
    if (!selectedDate) {
      return StepOneError.MISSING_DATE;
    }
    if (!selectedDate.times.find((x) => x.seconds === values.timeSeconds)) {
      return StepOneError.MISSING_TIME;
    }
    return undefined;
  }
  return values.privateOrderEndTimeSeconds > values.timeSeconds ? undefined : StepOneError.END_BEFORE_START;
};

type FirstIncompleteStepArgs = {
  stepOneError?: StepOneError;
  // Names of the failing fields that belong to the customer step.
  customerErrors: string[];
};

export const firstIncompleteStep = ({ stepOneError, customerErrors }: FirstIncompleteStepArgs): WizardStep => {
  if (stepOneError) {
    return WizardStep.WhatWhen;
  }
  return customerErrors.length > 0 ? WizardStep.Customer : WizardStep.Extras;
};

type StepThreeValues = {
  createPaymentLink: boolean;
  upfrontAmount: number;
  model: PaymentModel;
  orderPrice: number;
};

// A payment link charges the up-front amount, so it must be above zero, and (outside a deposit) at most the order
// price.
export const validateStepThree = ({
  createPaymentLink,
  upfrontAmount,
  model,
  orderPrice,
}: StepThreeValues): StepThreeError | undefined => {
  if (!createPaymentLink) {
    return undefined;
  }
  if (upfrontAmount <= 0) {
    return StepThreeError.LINK_AMOUNT_REQUIRED;
  }
  return model !== PaymentModel.Deposit && upfrontAmount > orderPrice
    ? StepThreeError.LINK_AMOUNT_ABOVE_PRICE
    : undefined;
};
